import { useMemo, useState } from "react";

import {
  AccountEltDestinationFragment,
  EltDestinationsDocument,
  EltSyncDestinationFragment,
  EltSyncDestinationReload,
  EltSyncDestinationsDocument,
  IdentifierCasing,
  useAddEltSyncDestinationMutation,
} from "@/apollo/types";
import { PrimaryButton, SecondaryButton } from "@/components/elements/Button";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import Select from "@/components/elements/Select";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";
import { InputError } from "@/components/primitives/InputError";
import { Input } from "@/components/primitives/input";
import { IntegrationLogo } from "@/integrations";
import { useToast } from "@/providers/ToastProvider";

import { SkippedFeaturesAlert } from "./SkippedFeaturesAlert";
import { defaultDestinationSchemaName } from "./defaultSchemaName";
import { destinationCasing } from "./destinationSettings";
import { getSchemaNameError } from "./schemaNameError";
import { skippedFeatures } from "./skippedFeatures";

export type SyncForDestinations = {
  id: string;
  destinationSchemaName?: string | null;
  identifierCasing?: IdentifierCasing | null;
  sourceIntegrationId: string;
  sourceStreams: {
    softDeleteEnabled?: boolean | null;
    enabledHistoryTable?: boolean | null;
    changeDataCaptureEnabled?: boolean | null;
  }[];
};

export const destinationLabel = (destination: AccountEltDestinationFragment) =>
  destination.label;

export function AddSyncDestinationDialog(props: {
  isOpen: boolean;
  onClose: () => void;
  sync: SyncForDestinations;
  accountDestinations: AccountEltDestinationFragment[];
  syncDestinations: EltSyncDestinationFragment[];
}) {
  return (
    <Modal centerOnScreen={false} isOpen={props.isOpen} onClose={props.onClose}>
      <ModalHeader>Add destination</ModalHeader>
      <ModalCloseButton />
      {props.isOpen && <AddSyncDestinationForm {...props} />}
    </Modal>
  );
}

function AddSyncDestinationForm(props: {
  onClose: () => void;
  sync: SyncForDestinations;
  accountDestinations: AccountEltDestinationFragment[];
  syncDestinations: EltSyncDestinationFragment[];
}) {
  const toast = useToast();
  const candidates = useMemo(() => {
    const added = new Set(props.syncDestinations.map((d) => d.destination.id));
    return props.accountDestinations.filter((d) => !added.has(d.id));
  }, [props.accountDestinations, props.syncDestinations]);

  const [destination, setDestination] =
    useState<AccountEltDestinationFragment>();
  const [schemaName, setSchemaName] = useState("");
  const [schemaNameError, setSchemaNameError] = useState<string | null>(null);
  const hasCdcStreams = props.sync.sourceStreams.some(
    (s) => s.changeDataCaptureEnabled,
  );
  const [reload, setReload] = useState(EltSyncDestinationReload.Continue);

  const [addEltSyncDestination, { loading: saving }] =
    useAddEltSyncDestinationMutation({
      refetchQueries: [
        {
          query: EltSyncDestinationsDocument,
          variables: { eltSyncId: props.sync.id },
        },
        { query: EltDestinationsDocument },
      ],
      awaitRefetchQueries: true,
      onCompleted: ({ addEltSyncDestination }) => {
        toast(
          "Destination added",
          `The sync now also loads into ${destinationLabel(addEltSyncDestination.destination)}.`,
          "success",
        );
        props.onClose();
      },
      onError: (e) => {
        const fieldError = getSchemaNameError(e);
        if (fieldError) {
          setSchemaNameError(fieldError);
          return;
        }
        toast("Destination not added", e.message, "error");
      },
    });

  const defaultSchemaName = defaultDestinationSchemaName(
    props.sync.destinationSchemaName ?? props.sync.sourceIntegrationId,
    props.sync.identifierCasing,
    destination ? destinationCasing(destination) : null,
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!destination) return;
        addEltSyncDestination({
          variables: {
            input: {
              eltSyncId: props.sync.id,
              eltDestinationId: destination.id,
              destinationSchemaName: schemaName.trim() || null,
              reload,
            },
          },
        });
      }}
    >
      <ModalBody className="flex flex-col gap-4">
        <div>
          <FieldLabel htmlFor="syncDestination">Destination</FieldLabel>
          <Select
            inputId="syncDestination"
            autoFocus
            placeholder="Select an ELT destination..."
            noOptionsMessage={() =>
              "The sync already loads into every ELT destination"
            }
            options={candidates}
            value={destination}
            getOptionLabel={destinationLabel}
            onChange={(item?: AccountEltDestinationFragment) =>
              setDestination(item)
            }
            iconParser={(item: AccountEltDestinationFragment) => (
              <IntegrationLogo id={item.integrationId} className="h-6" />
            )}
          />
        </div>
        <div>
          <FieldLabel htmlFor="syncDestinationSchemaName">
            Destination schema
          </FieldLabel>
          <Input
            id="syncDestinationSchemaName"
            size="md"
            placeholder={defaultSchemaName}
            value={schemaName}
            pattern="^[A-Za-z0-9_]*$"
            title="Letters, numbers and underscores only."
            aria-invalid={!!schemaNameError}
            onChange={(e) => {
              setSchemaName(e.target.value);
              setSchemaNameError(null);
            }}
          />
          {schemaNameError && (
            <InputError className="mt-1">{schemaNameError}</InputError>
          )}
          <InputDescription className="mt-1">
            Leave empty to use {defaultSchemaName}. Fixed once added.
          </InputDescription>
        </div>
        <fieldset>
          <legend className="mb-1 text-xs leading-6 font-semibold">
            Existing data
          </legend>
          <div className="flex flex-col gap-2 text-sm">
            <ReloadOption
              checked={reload === EltSyncDestinationReload.Continue}
              onChange={() => setReload(EltSyncDestinationReload.Continue)}
              label="Continue from the next sync"
              description="Loads only what the next syncs pick up. Rows synced before stay out of this destination."
            />
            <ReloadOption
              checked={reload === EltSyncDestinationReload.FullSync}
              onChange={() => setReload(EltSyncDestinationReload.FullSync)}
              disabled={hasCdcStreams}
              label="Full sync every table"
              description={
                hasCdcStreams
                  ? "Not available: this sync has tables with change data capture, which can only continue."
                  : "Reloads every table into every destination of this sync, the data warehouse included."
              }
            />
          </div>
        </fieldset>
        {destination && (
          <SkippedFeaturesAlert
            destinationLabel={destinationLabel(destination)}
            warnings={skippedFeatures(
              destination,
              props.sync.sourceStreams,
              props.sync.sourceIntegrationId,
            )}
          />
        )}
      </ModalBody>
      <ModalFooter className="flex-row-reverse justify-start gap-4">
        <PrimaryButton type="submit" disabled={!destination} isLoading={saving}>
          Add
        </PrimaryButton>
        <SecondaryButton onClick={props.onClose}>Cancel</SecondaryButton>
      </ModalFooter>
    </form>
  );
}

function ReloadOption(props: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  label: string;
  description: string;
}) {
  return (
    <label
      className={
        props.disabled
          ? "flex cursor-not-allowed gap-2 opacity-60"
          : "flex gap-2"
      }
    >
      <input
        type="radio"
        name="reload"
        className="mt-0.5 form-radio"
        checked={props.checked}
        disabled={props.disabled}
        onChange={props.onChange}
      />
      <span>
        <span className="block font-medium">{props.label}</span>
        <span className="block text-xs text-muted-foreground">
          {props.description}
        </span>
      </span>
    </label>
  );
}
